import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/AppIcon';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import { useContactInfo } from '../utils/contactObfuscation';

const Privacy = () => {
  const contactInfo = useContactInfo();
  
  // SEO Data
  const seoData = {
    title: 'Privacy Policy - OctalChip | Web & Mobile App Development',
    description: 'Privacy Policy for OctalChip - Learn how we collect, use, and protect your personal information when using our web and mobile app development services.',
    keywords: 'privacy policy, data protection, personal information, OctalChip, web development, mobile app development, data security, GDPR compliance, privacy rights',
    canonical: 'https://www.octalchip.com/privacy',
    tags: ['privacy policy', 'data protection', 'personal information', 'OctalChip'],
    additionalMeta: [
      { name: 'theme-color', content: '#00d690' },
      { name: 'mobile-web-app-capable', content: 'yes' }
    ],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'Privacy Policy', url: 'https://www.octalchip.com/privacy' }
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Privacy Policy - OctalChip",
      "description": "Privacy Policy for OctalChip - Learn how we collect, use, and protect your personal information when using our web and mobile app development services.",
      "url": "https://www.octalchip.com/privacy",
      "mainEntity": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "404, Opera point, kothariya",
          "addressLocality": "Rajkot",
          "addressRegion": "Gujarat",
          "postalCode": "360022",
          "addressCountry": "IN"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "business@octalchip.com"
        }
      }
    }
  };

  return (
    <>
      <SEO {...seoData} />
      
      <Header />
      
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-10">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mb-3 sm:mb-4">
              <Icon name="ArrowLeft" size={14} className="sm:w-4 sm:h-4 mr-2" />
              <span className="text-sm sm:text-base">Back to Home</span>
            </Link>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-xs sm:text-sm md:text-base text-gray-600">Last updated: January 27, 2025</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
            <section>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">1. Introduction</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3 sm:mb-4">
                Welcome to OctalChip ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using our website or services, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">2.1 Personal Information</h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-2 sm:mb-3">
                We may collect personal information that you voluntarily provide to us, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                <li>Name and contact information (email address, phone number)</li>
                <li>Company information and job title</li>
                <li>Project requirements and specifications</li>
                <li>Communication preferences</li>
                <li>Payment information (processed securely through third-party providers)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Automatically Collected Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                When you visit our website, we may automatically collect certain information, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>IP address and location data</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website information</li>
                <li>Device information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use the collected information for various purposes, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Providing and improving our web and mobile app development services</li>
                <li>Communicating with you about projects and services</li>
                <li>Processing payments and managing billing</li>
                <li>Sending newsletters and marketing communications (with your consent)</li>
                <li>Analyzing website usage to improve user experience</li>
                <li>Complying with legal obligations</li>
                <li>Protecting against fraud and ensuring security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>With your explicit consent</li>
                <li>To trusted service providers who assist us in operating our business</li>
                <li>When required by law or to protect our rights</li>
                <li>In connection with a business transfer or acquisition</li>
                <li>To prevent fraud or security threats</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>SSL encryption for data transmission</li>
                <li>Secure servers and databases</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication systems</li>
                <li>Employee training on data protection</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use cookies and similar tracking technologies to enhance your browsing experience. You can control cookie settings through your browser preferences. Types of cookies we use include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Essential cookies for website functionality</li>
                <li>Analytics cookies to understand user behavior</li>
                <li>Marketing cookies for targeted advertising (with consent)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Right to access your personal information</li>
                <li>Right to correct inaccurate information</li>
                <li>Right to delete your personal information</li>
                <li>Right to restrict processing of your information</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
                <li>Right to withdraw consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>OctalChip</strong></p>
                <p className="text-gray-700 mb-2">404, Opera Point, Kothariya, Gujarat, India, 360022</p>
                <p className="text-gray-700 mb-2">Email: <a href="mailto:business@octalchip.com" className="text-primary hover:underline">business@octalchip.com</a></p>
                {contactInfo.phoneLink && (
                  <p className="text-gray-700">Phone: <a href={contactInfo.phoneLink} className="text-primary hover:underline">{contactInfo.phone}</a></p>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default Privacy;

